// src/pages/Admin/AdminDocumentationEmailSubscribers.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  TextField,
  Typography,
  Grid,
  Autocomplete,
  Chip,
  Alert,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import Datagrid from "../../components/Datagrid/Datagrid";
import ModalWrapper from "../../components/ModalWrapper/ModalWrapper";
import UserName from "../../components/UserName/UserName";
import { getAllUsers } from "../../services/analyticsService";
import { getCustomersCusSites } from "../../services/manageCustomerMasterService";
import { getAllCustomerSites } from "../../services/manageSitesMasterService";
import { useAuth } from "../../context/authContext";
import useActorAllocationScope from "../../hooks/useActorAllocationScope";
import DataGridSkeletonForEmailSubscribers from "../../components/PageSkeletons/DataGridSkeletonForEmailSubscribers";

const toBool = (value, fallback = false) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "on", "enabled"].includes(normalized)) return true;
    if (["false", "0", "no", "off", "disabled"].includes(normalized)) return false;
  }
  return fallback;
};

const normalizePermissionsObject = (permissions) => {
  if (!permissions) return {};
  if (typeof permissions === "string") {
    try {
      const parsed = JSON.parse(permissions);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (_error) {
      return {};
    }
  }
  return permissions && typeof permissions === "object" ? permissions : {};
};

const AdminDocumentationEmailSubscribers = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const {
    scopeLoading,
    actorSiteIds,
    actorCustomerIds,
    actorHasAllSiteVisibility,
  } = useActorAllocationScope();

  const actorRoleType = useMemo(
    () => String(user?.role?.role_type || user?.role?.role_name || user?.user_role_name || user?.user_role || "").trim().toLowerCase(),
    [user?.role?.role_type, user?.role?.role_name, user?.user_role_name, user?.user_role]
  );
  const actorRolePermissions = useMemo(() => normalizePermissionsObject(user?.role?.permissions), [user?.role?.permissions]);
  const actorUserManagementPerms = useMemo(() => actorRolePermissions?.user_management || {}, [actorRolePermissions]);
  const actorViewScope = useMemo(
    () => String(actorUserManagementPerms?.view_scope || (actorRoleType === "admin" ? "all" : "all")).trim().toLowerCase(),
    [actorRoleType, actorUserManagementPerms?.view_scope]
  );
  const actorCanViewPendingUsersForApproval = useMemo(
    () => toBool(actorUserManagementPerms?.can_view_pending_users_for_approval, false),
    [actorUserManagementPerms?.can_view_pending_users_for_approval]
  );
  const actorApprovedDomains = useMemo(() => {
    const domainScope = String(actorUserManagementPerms?.domain_user_management_scope || "approved_domains_only").trim().toLowerCase();
    if (domainScope === "any_domain") return [];
    const raw = String(user?.approved_domains || user?.user_meta?.approved_domains || "").trim();
    if (!raw) return [];
    return raw
      .split(",")
      .map((domain) => domain.trim().replace(/^@/, "").toLowerCase())
      .filter(Boolean);
  }, [actorUserManagementPerms?.domain_user_management_scope, user?.approved_domains, user?.user_meta?.approved_domains]);

  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [customersOptions, setCustomersOptions] = useState([]);
  const [sitesOptions, setSitesOptions] = useState([]);
  const [customerValue, setCustomerValue] = useState(null);
  const [locationValue, setLocationValue] = useState(null);
  const allCustomersOption = useMemo(() => ({ value: null, label: t("EMAIL_SUBSCRIBERS.ALL_CUSTOMERS") }), [t]);
  const allLocationsOption = useMemo(() => ({ value: null, label: t("EMAIL_SUBSCRIBERS.ALL_LOCATIONS") }), [t]);
  const [rows, setRows] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  const [subscriberModalOpen, setSubscriberModalOpen] = useState(false);

  const getRawUserEmailDomain = useCallback((userRow) => {
    const email = String(userRow?.user_email || "").trim().toLowerCase();
    if (!email || !email.includes("@")) return "";
    return email.split("@").pop() || "";
  }, []);

  const isRawUserWithinActorDomainScope = useCallback((userRow) => {
    if (!Array.isArray(actorApprovedDomains) || actorApprovedDomains.length === 0) return true;
    const domain = getRawUserEmailDomain(userRow);
    return !!domain && actorApprovedDomains.includes(domain);
  }, [actorApprovedDomains, getRawUserEmailDomain]);

  const getRawUserSiteIds = useCallback((userRow) => {
    const allocationSiteIds = Array.isArray(userRow?.allocations)
      ? userRow.allocations
          .map((allocation) => Number(allocation?.site_id))
          .filter((siteId) => Number.isFinite(siteId) && siteId > 0)
      : [];
    if (allocationSiteIds.length > 0) return Array.from(new Set(allocationSiteIds));
    const fallbackSiteIds = Array.isArray(userRow?.user_sites)
      ? userRow.user_sites.map((siteId) => Number(siteId)).filter((siteId) => Number.isFinite(siteId) && siteId > 0)
      : [];
    return Array.from(new Set(fallbackSiteIds));
  }, []);

  const canAccessCreatedScopeUser = useCallback(
    (userRow) => Number(userRow?.created_by) === Number(user?.user_id),
    [user?.user_id]
  );

  const isRawUserWithinActorSiteScope = useCallback((userRow) => {
    if (actorHasAllSiteVisibility) return true;
    const rowSiteIds = getRawUserSiteIds(userRow);
    const isPending = Number(userRow?.user_status) === 0;
    if (rowSiteIds.length === 0) {
      if (isPending && actorCanViewPendingUsersForApproval) {
        if (Array.isArray(actorApprovedDomains) && actorApprovedDomains.length > 0) {
          return isRawUserWithinActorDomainScope(userRow);
        }
        return true;
      }
      if (Array.isArray(actorApprovedDomains) && actorApprovedDomains.length > 0) {
        return isRawUserWithinActorDomainScope(userRow);
      }
      return false;
    }
    const actorSiteSet = new Set(
      (actorSiteIds || [])
        .map((siteId) => Number(siteId))
        .filter((siteId) => Number.isFinite(siteId) && siteId > 0)
    );
    return rowSiteIds.every((siteId) => actorSiteSet.has(Number(siteId)));
  }, [actorApprovedDomains, actorCanViewPendingUsersForApproval, actorHasAllSiteVisibility, actorSiteIds, getRawUserSiteIds, isRawUserWithinActorDomainScope]);

  const canViewRawUserByScope = useCallback((userRow) => {
    if (!userRow) return false;
    if (Number(user?.user_id) === 1) return true;
    if (Number(userRow?.user_status) === 0 && !actorCanViewPendingUsersForApproval) return false;
    if (!isRawUserWithinActorDomainScope(userRow)) return false;
    if (!isRawUserWithinActorSiteScope(userRow)) return false;
    if (Number(userRow?.user_id) === Number(user?.user_id)) return true;
    if (actorViewScope === "created_by_me") {
      return canAccessCreatedScopeUser(userRow);
    }
    return true;
  }, [actorCanViewPendingUsersForApproval, actorViewScope, canAccessCreatedScopeUser, isRawUserWithinActorDomainScope, isRawUserWithinActorSiteScope, user?.user_id]);

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await getCustomersCusSites();
      if (!response?.status) return [];

      const allCustomers = (response.data || []).filter(
        (customer) => customer.customer_name && customer.customer_name !== "EMPTY"
      );
      const filteredCustomers = actorHasAllSiteVisibility
        ? allCustomers
        : allCustomers.filter((customer) => actorCustomerIds.includes(Number(customer.cus_id)));

      const options = filteredCustomers.map((customer) => ({
        value: customer.cus_id,
        label: `${customer.customer_name} - ${customer.customer_number}`,
      }));

      setCustomers(filteredCustomers);
      setCustomersOptions(options);
      return filteredCustomers;
    } catch (error) {
      console.error("Error fetching customers:", error);
      setCustomers([]);
      setCustomersOptions([]);
      return [];
    }
  }, [actorCustomerIds, actorHasAllSiteVisibility]);

  const fetchSites = useCallback(async () => {
    try {
      const response = await getAllCustomerSites();
      if (!response?.status) return [];

      const allSites = (response.data || []).filter(
        (site) => site.customer_site_name && site.customer_site_name !== "EMPTY"
      );
      const filteredSites = actorHasAllSiteVisibility
        ? allSites
        : allSites.filter((site) => actorSiteIds.includes(Number(site.site_id)));

      const options = filteredSites.map((site) => ({
        value: site.site_id,
        label: `${site.customer_site_name} - ${site.customer_site_number}`,
      }));

      setSites(filteredSites);
      setSitesOptions(options);
      return filteredSites;
    } catch (error) {
      console.error("Error fetching sites:", error);
      setSites([]);
      setSitesOptions([]);
      return [];
    }
  }, [actorHasAllSiteVisibility, actorSiteIds]);

  const fetchUsers = useCallback(async (customerRows = [], siteRows = []) => {
    try {
      setLoadingUsers(true);
      const response = await getAllUsers({ cacheBust: Date.now() });
      if (response?.status && Array.isArray(response.data)) {
        const visibleUsers = (response.data || [])
  .filter((userRow) => Number(userRow?.user_id) !== 1)
  .filter((userRow) => canViewRawUserByScope(userRow));
        setRows(visibleUsers.map((userRow) => mapUser(userRow, customerRows, siteRows, t)));
        return;
      }
      setRows([]);
    } catch (error) {
      console.error("Error fetching users:", error);
      setRows([]);
    } finally {
      setLoadingUsers(false);
    }
  }, [canViewRawUserByScope, t]);

  useEffect(() => {
    if (!user || scopeLoading) return;

    let mounted = true;
    const init = async () => {
      setInitialLoading(true);
      setLoadingFilters(true);
      const [customerRows, siteRows] = await Promise.all([fetchCustomers(), fetchSites()]);
      if (!mounted) return;
      setLoadingFilters(false);
      await fetchUsers(customerRows, siteRows);
      if (mounted) setInitialLoading(false);
    };

    init();
    return () => {
      mounted = false;
    };
  }, [fetchCustomers, fetchSites, fetchUsers, scopeLoading, user]);

  useEffect(() => {
  const refreshSubscribers = async () => {
    const [customerRows, siteRows] = await Promise.all([
      fetchCustomers(),
      fetchSites(),
    ]);
    await fetchUsers(customerRows, siteRows);
  };

  window.addEventListener("user-sites-updated", refreshSubscribers);

  return () => {
    window.removeEventListener("user-sites-updated", refreshSubscribers);
  };
}, [fetchCustomers, fetchSites, fetchUsers]);
  const filteredSitesOptions = useMemo(() => {
    if (!customerValue?.value) return sitesOptions;
    return (sitesOptions || []).filter((option) => {
      const matchingSite = (sites || []).find((site) => Number(site.site_id) === Number(option.value));
      return Number(matchingSite?.cus_id) === Number(customerValue.value);
    });
  }, [customerValue?.value, sites, sitesOptions]);

  const getRowSiteIds = useCallback((row) => {
    if (!Array.isArray(row?.site_ids)) return [];
    return Array.from(
      new Set(
        row.site_ids
          .map((siteId) => Number(siteId))
          .filter((siteId) => Number.isFinite(siteId) && siteId > 0)
      )
    );
  }, []);

  const scopedRows = useMemo(() => {
    const selectedSiteId = Number(locationValue?.value);
    if (Number.isFinite(selectedSiteId) && selectedSiteId > 0) {
      return (rows || []).filter((row) => {
        const isGlobalRole = Boolean(row?.is_global_role);
        const rowSiteIds = getRowSiteIds(row);
        if (rowSiteIds.length === 0) return isGlobalRole;
        return rowSiteIds.includes(selectedSiteId);
      });
    }

    const selectedCustomerId = Number(customerValue?.value);
    if (Number.isFinite(selectedCustomerId) && selectedCustomerId > 0) {
      const selectedCustomerSiteIds = new Set(
        (sites || [])
          .filter((site) => Number(site?.cus_id) === selectedCustomerId)
          .map((site) => Number(site?.site_id))
          .filter((siteId) => Number.isFinite(siteId) && siteId > 0)
      );

      return (rows || []).filter((row) => {
        const isGlobalRole = Boolean(row?.is_global_role);
        const rowCustomerIds = Array.isArray(row?.cus_ids)
          ? row.cus_ids.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
          : [];
        if (rowCustomerIds.length > 0) {
          return rowCustomerIds.includes(selectedCustomerId);
        }
        const rowSiteIds = getRowSiteIds(row);
        if (rowSiteIds.length === 0) return isGlobalRole;
        return rowSiteIds.some((siteId) => selectedCustomerSiteIds.has(Number(siteId)));
      });
    }

    return rows || [];
  }, [customerValue, getRowSiteIds, locationValue, rows, sites]);

  const handleSubscriberSearch = useCallback(({ query, data }) => {
    const normalizedQuery = String(query || "").trim().toLowerCase();
    if (!normalizedQuery) return data;
    return (data || []).filter((row) => {
      const name = String(row?.name || "").toLowerCase();
      const email = String(row?.email || "").toLowerCase();
      return name.includes(normalizedQuery) || email.includes(normalizedQuery);
    });
  }, []);

  const renderNotificationChip = useCallback((enabled) => (
    <Chip
      label={enabled ? t("EMAIL_SUBSCRIBERS.ENABLED") : t("EMAIL_SUBSCRIBERS.DISABLED")}
      color={enabled ? "success" : "default"}
      size="small"
      variant={enabled ? "filled" : "outlined"}
    />
  ), [t]);

  const ellipsisCell = useMemo(() => ({
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    width: "100%",
  }), []);

  const renderTextCell = useCallback((params, color = "text.secondary") => (
    <Box sx={{ display: "flex", alignItems: "center", height: "100%", width: "100%" }}>
      <Typography
        variant="body2"
        color={color}
        sx={{
          fontSize: "0.875rem",
          ...ellipsisCell,
        }}
        title={params.value}
      >
        {params.value}
      </Typography>
    </Box>
  ), [ellipsisCell]);

  const renderSubscriberNameCell = useCallback((params) => (
    <Box sx={{ display: "flex", alignItems: "center", height: "100%", width: "100%" }}>
      <UserName
        size={30}
        name={params?.row?.name || t("EMAIL_SUBSCRIBERS.NA")}
      />
    </Box>
  ), [t]);

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: t("EMAIL_SUBSCRIBERS.NAME"),
        flex: 1.4,
        minWidth: 200,
        sortable: true,
        hideable: false,
        filterable: false,
        renderCell: renderSubscriberNameCell,
      },
      {
        field: "email",
        headerName: t("EMAIL_SUBSCRIBERS.EMAIL"),
        flex: 1.6,
        minWidth: 260,
        sortable: true,
        filterable: false,
        renderCell: (params) => renderTextCell(params, "text.primary"),
      },
      {
        field: "user_doc_added_email",
        headerName: t("EMAIL_SUBSCRIBERS.USER_DOC_ADDED_EMAIL"),
        flex: 1,
        minWidth: 190,
        sortable: true,
        filterable: false,
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%", width: "100%" }}>
            {renderNotificationChip(params.value)}
          </Box>
        ),
      },
      {
        field: "user_doc_modified_email",
        headerName: t("EMAIL_SUBSCRIBERS.USER_DOC_MODIFIED_EMAIL"),
        flex: 1,
        minWidth: 210,
        sortable: true,
        filterable: false,
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%", width: "100%" }}>
            {renderNotificationChip(params.value)}
          </Box>
        ),
      },
    ],
    [t, renderNotificationChip, renderSubscriberNameCell, renderTextCell]
  );

  const allocationColumns = useMemo(
    () => [
      { field: "customer_site_number", headerName: t("TABLE_HEADERS.SITE_NUMBER"), width: 160 },
      { field: "customer_site_name", headerName: t("LABELS.SITE_NAME"), flex: 1, minWidth: 220 },
      { field: "parent_customer_number", headerName: t("TABLE_HEADERS.CUSTOMER_NUMBER"), width: 180 },
      { field: "parent_customer_name", headerName: t("LABELS.CUSTOMER_NAME"), flex: 1, minWidth: 220 },
      {
        field: "user_doc_added_email",
        headerName: t("EMAIL_SUBSCRIBERS.USER_DOC_ADDED_EMAIL", { defaultValue: "Document Added" }),
        width: 180,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            {renderNotificationChip(params.row.user_doc_added_email)}
          </Box>
        ),
      },
      {
        field: "user_doc_modified_email",
        headerName: t("EMAIL_SUBSCRIBERS.USER_DOC_MODIFIED_EMAIL", { defaultValue: "Document Modified" }),
        width: 190,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            {renderNotificationChip(params.row.user_doc_modified_email)}
          </Box>
        ),
      },
    ],
    [t, renderNotificationChip]
  );

  const handleSubscriberRowClick = useCallback((params) => {
    if (!params?.row) return;
    setSelectedSubscriber(params.row);
    setSubscriberModalOpen(true);
  }, []);

  const selectedAllocationRows = useMemo(
    () =>
      (selectedSubscriber?.allocation_rows || []).map((row) => ({
        ...row,
        user_doc_added_email: selectedSubscriber?.user_doc_added_email,
        user_doc_modified_email: selectedSubscriber?.user_doc_modified_email,
      })),
    [selectedSubscriber]
  );

  const selectedAllocationGridHeight = useMemo(
    () => (selectedAllocationRows.length > 10 ? "min(52vh, 520px)" : 360),
    [selectedAllocationRows.length]
  );

  const loading = loadingUsers || loadingFilters;

  return (
    <Box sx={{ width: "100%" }}>
      {!initialLoading && (
        <Box
          sx={{
            mb: 2,
            mt: 1,
            display: "flex",
            flexDirection: "column",
            rowGap: "5px",
            background: "#eee",
            p: 2,
            borderRadius: "10px",
          }}
        >
          <Typography fontWeight={600} mb={0.5}>{t("EMAIL_SUBSCRIBERS.FILTERS_TITLE")}</Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {t("EMAIL_SUBSCRIBERS.FILTERS_DESCRIPTION")}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                size="small"
                options={[allCustomersOption, ...customersOptions]}
                value={customerValue}
                onChange={(_, nextValue) => {
                  setCustomerValue(nextValue?.value === null ? null : nextValue);
                  setLocationValue(null);
                }}
                isOptionEqualToValue={(option, value) => option.value === value?.value}
                getOptionLabel={(option) => option?.label || ""}
                loading={loadingFilters}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("EMAIL_SUBSCRIBERS.CUSTOMER")}
                    placeholder={t("EMAIL_SUBSCRIBERS.ALL_CUSTOMERS")}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Autocomplete
                size="small"
                options={[allLocationsOption, ...filteredSitesOptions]}
                value={locationValue}
                onChange={(_, nextValue) => {
                  setLocationValue(nextValue?.value === null ? null : nextValue);
                  if (nextValue?.value) {
                    const selectedSite = (sites || []).find((site) => Number(site.site_id) === Number(nextValue.value));
                    if (selectedSite?.cus_id) {
                      const matchingCustomer = (customersOptions || []).find(
                        (customer) => Number(customer.value) === Number(selectedSite.cus_id)
                      );
                      if (matchingCustomer) setCustomerValue(matchingCustomer);
                    }
                  }
                }}
                isOptionEqualToValue={(option, value) => option.value === value?.value}
                getOptionLabel={(option) => option?.label || ""}
                loading={loadingFilters}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("EMAIL_SUBSCRIBERS.LOCATION")}
                    placeholder={t("EMAIL_SUBSCRIBERS.ALL_LOCATIONS")}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      <Box sx={{ px: 1, maxWidth: "100%" }}>
        <Box
          sx={{
            width: "100%",
            overflow: "auto",
            px: 1,
            "&::-webkit-scrollbar": {
              height: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#888",
              borderRadius: "4px",
            },
          }}
        >
          <Box sx={{ minWidth: "960px" }}>
            {initialLoading ? (
              <DataGridSkeletonForEmailSubscribers />
            ) : (
              <Datagrid
                data={scopedRows}
                columns={columns}
                rowIdField="id"
                checkboxSelection={false}
                showSearchBar={true}
                handleSearch={handleSubscriberSearch}
                autoHeight={true}
                pageSize={10}
                loading={loading}
                rowClick
                onRowClick={handleSubscriberRowClick}
              />
            )}
          </Box>
        </Box>
      </Box>

      <ModalWrapper
        open={subscriberModalOpen}
        setOpen={setSubscriberModalOpen}
        onClose={() => setSelectedSubscriber(null)}
        title={t("EMAIL_SUBSCRIBERS.DOCUMENT_SUBSCRIPTION_TITLE", { defaultValue: "Document Subscription" })}
        subtitle={
          selectedSubscriber ? (
            <Box>
              <Typography variant="body2" color="text.primary">
                {`Name : ${selectedSubscriber.name || ""}`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {`Email : ${selectedSubscriber.email || ""}`}
              </Typography>
            </Box>
          ) : ""
        }
        width="70vw"
        content={
          <Box sx={{ minWidth: 0, maxHeight: "min(62vh, 580px)", overflow: "hidden" }}>
            {selectedSubscriber?.is_global_role ? (
              <Alert severity="info">
                {t(
                  "USER_MANAGEMENT.APPROVED_ADMIN_ALL_LOCATIONS_INFO",
                  { defaultValue: "This approved admin has access to all sites, so there are no specific allocated sites to list." }
                )}
              </Alert>
            ) : selectedAllocationRows.length > 0 ? (
              <Box
                sx={{
                  minWidth: 0,
                  "& > div": { marginBottom: "0 !important" },
                  "& .MuiDataGrid-root": { marginTop: "0 !important" },
                }}
              >
                <Datagrid
                  data={selectedAllocationRows}
                  columns={allocationColumns}
                  rowIdField="id"
                  checkboxSelection={false}
                  showSearchBar={selectedAllocationRows.length > 10}
                  autoHeight={false}
                  gridHeight={selectedAllocationGridHeight}
                  pageSize={10}
                  pageSizeOptions={[5, 10, 25, 50]}
                />
              </Box>
            ) : (
              <Alert severity="info">{t("UI_TEXT.NO_ALLOCATED_LOCATIONS")}</Alert>
            )}
          </Box>
        }
      />
    </Box>
  );
};

function formatNameAndNumber(name, number, fallback) {
  const cleanName = String(name || "").trim();
  const cleanNumber = String(number || "").trim();
  if (cleanName && cleanNumber) return `${cleanName} - ${cleanNumber}`;
  return cleanName || cleanNumber || fallback;
}

function getAllocationMeta(allocation, customers, sites, t) {
  const siteId = Number(allocation?.site_id);
  const allocationCustomerId = Number(allocation?.cus_id);
  const matchedSite = Number.isFinite(siteId) && siteId > 0
    ? (sites || []).find((site) => Number(site.site_id) === siteId)
    : null;
  const customerId = Number.isFinite(allocationCustomerId) && allocationCustomerId > 0
    ? allocationCustomerId
    : Number(matchedSite?.cus_id);
  const matchedCustomer = Number.isFinite(customerId) && customerId > 0
    ? (customers || []).find((customer) => Number(customer.cus_id) === customerId)
    : null;

  const customerName =
    matchedCustomer?.customer_name ||
    matchedSite?.parent_customer_name ||
    allocation?.parent_customer_name ||
    "";
  const customerNumber =
    matchedCustomer?.customer_number ||
    matchedSite?.parent_customer_number ||
    allocation?.parent_customer_number ||
    "";
  const siteName =
    matchedSite?.customer_site_name ||
    allocation?.customer_site_name ||
    allocation?.site_name ||
    "";
  const siteNumber =
    matchedSite?.customer_site_number ||
    allocation?.customer_site_number ||
    allocation?.site_number ||
    "";

  return {
    cus_id: Number.isFinite(customerId) && customerId > 0 ? customerId : null,
    site_id: Number.isFinite(siteId) && siteId > 0 ? siteId : null,
    customer_name: customerName,
    customer_number: customerNumber,
    site_name: siteName,
    site_number: siteNumber,
    bakery: formatNameAndNumber(customerName, customerNumber, t("EMAIL_SUBSCRIBERS.NA")),
    bakery_location: formatNameAndNumber(siteName, siteNumber, t("EMAIL_SUBSCRIBERS.NA")),
  };
}

function buildAllocationRows({ allocations, isGlobalRole, customers, sites, t }) {
  if (isGlobalRole) {
    return [];
  }

  return (allocations || [])
    .map((allocation, index) => {
      const meta = getAllocationMeta(allocation, customers, sites, t);
      return {
        id: `${meta.cus_id || "customer"}-${meta.site_id || "site"}-${index}`,
        customer_site_number: meta.site_number || (meta.site_id ? String(meta.site_id) : t("EMAIL_SUBSCRIBERS.NA")),
        customer_site_name: meta.site_name || t("EMAIL_SUBSCRIBERS.NA"),
        parent_customer_number: meta.customer_number || (meta.cus_id ? String(meta.cus_id) : t("EMAIL_SUBSCRIBERS.NA")),
        parent_customer_name: meta.customer_name || t("EMAIL_SUBSCRIBERS.NA"),
        cus_id: meta.cus_id,
        site_id: meta.site_id,
      };
    })
    .filter((row) => row.customer_site_number || row.customer_site_name || row.parent_customer_number || row.parent_customer_name);
}

function mapUser(userRow, customers, sites, t) {
  let notificationPrefs = {
    USER_NEW_DOCUMENT_ADDED: { email: false, inapp: false },
    USER_DOCUMENT_MODIFIED: { email: false, inapp: false },
  };

  try {
    const rawPreferences = userRow.user_meta?.notification_preferences;
    if (rawPreferences) {
      const parsed = JSON.parse(rawPreferences);
      if (parsed.USER_NEW_DOCUMENT_ADDED) notificationPrefs.USER_NEW_DOCUMENT_ADDED = parsed.USER_NEW_DOCUMENT_ADDED;
      if (parsed.USER_DOCUMENT_MODIFIED) notificationPrefs.USER_DOCUMENT_MODIFIED = parsed.USER_DOCUMENT_MODIFIED;
    }
  } catch (error) {
    console.error("Failed to parse notification_preferences for user", userRow.user_id, error);
  }

  const allocations = Array.isArray(userRow.allocations) ? userRow.allocations : [];
  const customerIds = Array.from(
    new Set(
      allocations
        .map((allocation) => Number(allocation.cus_id))
        .filter((customerId) => Number.isFinite(customerId))
    )
  );
  const siteIds = Array.from(
    new Set(
      allocations
        .map((allocation) => Number(allocation.site_id))
        .filter((siteId) => Number.isFinite(siteId))
    )
  );

  const numericRole = Number(userRow.user_role);
  const isAllSiteRole = numericRole === 1 || numericRole === 2 || String(userRow.user_role) === "2";
  const isGlobalRole = isAllSiteRole && allocations.length === 0;

  const displayName =
    userRow.user_display_name ||
    (userRow.user_first_name && userRow.user_last_name
      ? `${userRow.user_first_name} ${userRow.user_last_name}`.trim()
      : userRow.user_name || t("EMAIL_SUBSCRIBERS.NA"));

  const baseRow = {
    id: userRow.user_id,
    user_id: userRow.user_id,
    name: displayName,
    email: userRow.user_email,
    role: userRow.user_role_name || t("EMAIL_SUBSCRIBERS.NA"),
    user_doc_added_email: notificationPrefs.USER_NEW_DOCUMENT_ADDED.email,
    user_doc_added_inapp: notificationPrefs.USER_NEW_DOCUMENT_ADDED.inapp,
    user_doc_modified_email: notificationPrefs.USER_DOCUMENT_MODIFIED.email,
    user_doc_modified_inapp: notificationPrefs.USER_DOCUMENT_MODIFIED.inapp,
  };
  const allocationRows = buildAllocationRows({ allocations, isGlobalRole, customers, sites, t });
  const bakery = isGlobalRole
    ? "Global"
    : Array.from(new Set(allocationRows.map((row) => row.parent_customer_name).filter(Boolean))).join(", ") || t("EMAIL_SUBSCRIBERS.NA");
  const bakeryLocation = isGlobalRole
    ? "Global"
    : Array.from(new Set(allocationRows.map((row) => row.customer_site_name).filter(Boolean))).join(", ") || t("EMAIL_SUBSCRIBERS.NA");

  return {
    ...baseRow,
    is_global_role: isGlobalRole,
    bakery,
    bakery_location: bakeryLocation,
    allocation_rows: allocationRows,
    cus_ids: customerIds,
    site_ids: siteIds,
  };
}

export default AdminDocumentationEmailSubscribers;
